import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User } from "../types/api";

interface UserMenuProps {
  user: User | null;
  isPIAnywhere: boolean;
  hasProjects: boolean;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  isPIAnywhere,
  hasProjects,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleMenuItemClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const isAdmin = user?.role === "manager" || isPIAnywhere;
  const isInAdminArea = location.pathname.startsWith("/admin");
  const showBackToProjects =
    !isAdmin && hasProjects && location.pathname !== "/projects" && location.pathname !== "/login";

  return (
    <div className="relative" ref={menuRef}>
      <style>{`
        .user-menu-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #fff;
          border: 1.5px solid #D8D1C1;
          border-radius: 9999px;
          color: #0F2E2E;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .user-menu-button:hover {
          border-color: #0D9488;
          color: #0D9488;
        }

        .user-menu-button.open {
          border-color: #0D9488;
          color: #0D9488;
        }

        .user-menu-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 220px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          z-index: 50;
          animation: slideDown 0.2s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .user-menu-header {
          padding: 12px 16px;
          background: linear-gradient(135deg, #0F2E2E 0%, #14403C 100%);
          color: white;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .user-menu-name {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 2px;
        }

        .user-menu-institution {
          font-size: 12px;
          opacity: 0.9;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-style: bold
        }

        .user-menu-email {
          font-size: 12px;
          opacity: 0.9;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .user-menu-items {
          padding: 4px 0;
        }

        .user-menu-item {
          width: 100%;
          padding: 10px 16px;
          text-align: left;
          background: none;
          border: none;
          color: #333;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .user-menu-item:hover {
          background: #f8f9fa;
          color: #0D9488;
        }

        .user-menu-item.current {
          background: #CCFBF1;
          color: #0D9488;
          font-weight: 500;
        }

        .user-menu-divider {
          height: 1px;
          background: #e9ecef;
          margin: 4px 0;
        }

        .user-menu-item.logout {
          color: #dc3545;
        }

        .user-menu-item.logout:hover {
          background: #fff5f5;
          color: #c82333;
        }

        .chevron-icon {
          transition: transform 0.2s;
        }

        .chevron-icon.open {
          transform: rotate(180deg);
        }
      `}</style>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`user-menu-button ${isOpen ? "open" : ""}`}
      >
        <span>{user?.name || "User"}</span>
        <svg
          className={`chevron-icon ${isOpen ? "open" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-menu-name">{user?.name}</div>
            <div className="user-menu-institution">{user?.institution}</div>
            <div className="user-menu-email">{user?.email}</div>
          </div>

          <div className="user-menu-items">
            {/* Admin Dashboard */}
            {isAdmin && (
              <button
                onClick={() => handleMenuItemClick(() => navigate("/admin"))}
                className={`user-menu-item ${location.pathname.startsWith("/admin") ? "current" : ""}`}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                Admin Dashboard
              </button>
            )}

            {/* Data Source Portal - for data_uploader role */}
            {user?.role === "data_uploader" && (
              <button
                onClick={() => handleMenuItemClick(() => navigate("/data-source"))}
                className={`user-menu-item ${location.pathname.startsWith("/data-source") ? "current" : ""}`}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                  <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                  <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                </svg>
                Data Source Portal
              </button>
            )}

            {/* Browse Datasets - for researchers/PIs/admins (not external uploaders) */}
            {(hasProjects || isAdmin) && (
              <button
                onClick={() => handleMenuItemClick(() => navigate("/datasets"))}
                className={`user-menu-item ${location.pathname === "/datasets" ? "current" : ""}`}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                  <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                  <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                </svg>
                Browse Datasets
              </button>
            )}

            {/* Data Requests - only for users with project assignments */}
            {hasProjects && (
              <button
                onClick={() => handleMenuItemClick(() => navigate("/requests"))}
                className={`user-menu-item ${location.pathname === "/requests" ? "current" : ""}`}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                    clipRule="evenodd"
                  />
                </svg>
                Data Requests
              </button>
            )}

            {/* My Access Requests - only for users with project assignments */}
            {hasProjects && (
              <button
                onClick={() => handleMenuItemClick(() => navigate("/my-requests"))}
                className={`user-menu-item ${location.pathname === "/my-requests" ? "current" : ""}`}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  />
                </svg>
                My Access Requests
              </button>
            )}

            {/* Navigation */}
            {showBackToProjects && !isInAdminArea && (
              <>
                <div className="user-menu-divider" />
                <button
                  onClick={() => handleMenuItemClick(() => navigate("/projects"))}
                  className={`user-menu-item ${location.pathname === "/projects" ? "current" : ""}`}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {isInAdminArea ? "My Projects" : "Back to Projects"}
                </button>
              </>
            )}

            <div className="user-menu-divider" />

            {/* Settings */}
            <button
              onClick={() => handleMenuItemClick(() => navigate("/settings"))}
              className={`user-menu-item ${location.pathname === "/settings" ? "current" : ""}`}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                  clipRule="evenodd"
                />
              </svg>
              Settings
            </button>

            {/* Logout */}
            <button
              onClick={() => handleMenuItemClick(onLogout)}
              className="user-menu-item logout"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                  clipRule="evenodd"
                />
              </svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
